// src/pages/Dashboard.js - VERSIÓN CON RESTRICCIÓN DE GANANCIAS
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

// Servicios
import dashboardService from '../services/dashboard.service';
import productosService from '../services/productos.service';
import reportesService from '../services/reportes.service';
import ventasService from '../services/ventas.service';
import configuracionService from '../services/configuracion.service';

// Contexto de autenticación
import { useAuth } from '../contexts/AuthContext';

// Componentes
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Spinner from '../components/common/Spinner';
import DashboardDeudaEjecutivo from '../components/modules/dashboard/DashboardDeudaEjecutivo';
import IniciarPreVentaModal from '../components/modules/ventas/IniciarPreVentaModal';
import ZonasABMModal from '../components/modules/ventas/ZonasABMModal';
import PreVentaWizard from '../components/modules/ventas/PreVentaWizard';
import IniciarRepartoModal from '../components/modules/ventas/IniciarRepartoModal';
import RepartoWizardMovil from '../components/modules/ventas/RepartoWizardMovil';
import preventaService from '../services/preventa.service';
import CatalogActivationMetrics from '../components/modules/productos/CatalogActivationMetrics';
import useWorkspaceQuickAccess from '../hooks/useWorkspaceQuickAccess';
import GananciasFinancieraCard from '../components/modules/finanzas/GananciasFinancieraCard';
import CoberturaFinancieraCard from '../components/modules/finanzas/CoberturaFinancieraCard';
import CalidadFinancieraCard from '../components/modules/finanzas/CalidadFinancieraCard';
import {
  buildResumenDeudaEjecutivo,
  normalizarDeudaVencidaDias,
  parseCuentasPorCobrarResponse
} from '../utils/dashboardDeuda';

// Iconos
import { 
  FaShoppingCart, FaBoxOpen, FaExclamationTriangle, 
  FaChartLine, FaDollarSign, FaChartLine as FaTrendingUp, FaPlus,
  FaUser, FaFileInvoiceDollar, FaCalendarAlt,
  FaStore, FaArrowUp, FaArrowDown, FaPercent, FaTruck,
  FaClipboardCheck,
  FaStore as FaCentroComercial
} from 'react-icons/fa';

/**
 * Dashboard principal con restricción de ganancias
 */
const Dashboard = () => {
  const { currentUser, sucursalSeleccionada, hasPermission, orgId, isAuthenticated, loadingSucursales } = useAuth();
  const { canPuntoVenta } = useWorkspaceQuickAccess();
  const isDemoUser = /@nexopos\.demo\.local$/i.test(String(currentUser?.email || '').trim());
  const loadSeqRef = useRef(0);
  const dashboardErrorNotifiedRef = useRef(false);
  const dashboardRetryCountRef = useRef(0);

  const puedeVerGanancias = hasPermission('reportes', 'ver_ganancias');
  
  // Estados con inicializaciones seguras
  const [loading, setLoading] = useState(true);
  const [catalogActivation, setCatalogActivation] = useState(null);
  const [metricasMes, setMetricasMes] = useState({
    cantidadVentas: null,
    facturacion: null,
    ganancias: null,
    gastos: null
  });
  const [finanzasMes, setFinanzasMes] = useState({
    cobertura: null,
    calidadFinanciera: null,
    alertas: [],
    actualizadoEn: null
  });
  const [erroresMetricas, setErroresMetricas] = useState({});
  const [periodoMesLabel, setPeriodoMesLabel] = useState('');
  const [stats, setStats] = useState({
    totalProductos: 0,
    ventasHoy: 0,
    cantidadVentasHoy: 0,
    gananciasHoy: 0,
    stockBajo: 0,
    ticketPromedio: 0,
    margenPromedio: 0
  });
  
  // Estado para comparativos
  const [comparativos, setComparativos] = useState({
    ventasAyer: 0,
    gananciasAyer: 0,
    ventasSemanaAnterior: 0
  });

  // Deuda ejecutiva (cuenta corriente)
  const [deudaEjecutiva, setDeudaEjecutiva] = useState({
    resumen: { cantidadClientes: 0, totalAdeudado: 0, totalVencido: 0 },
    clientes: [],
    diasCriterio: 30
  });
  const [loadingDeudaEjecutiva, setLoadingDeudaEjecutiva] = useState(false);
  const [errorDeudaEjecutiva, setErrorDeudaEjecutiva] = useState(null);

  // Reparto
  const [modalRepartoOpen, setModalRepartoOpen] = useState(false);
  const [repartoSesion, setRepartoSesion] = useState(null);
  const [repartoWizardOpen, setRepartoWizardOpen] = useState(false);
  // Pre-venta
  const [modalPreventaOpen, setModalPreventaOpen] = useState(false);
  const [preventaSesion, setPreventaSesion] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [zonasModalOpen, setZonasModalOpen] = useState(false);
  const [resumenPreventa, setResumenPreventa] = useState([]);
  const [confirmandoId, setConfirmandoId] = useState(null);

  /**
   * Carga datos con manejo robusto de errores
   */
  useEffect(() => {
    // Evita ráfagas al entrar: esperamos sesión y contexto mínimo listos.
    if (!isAuthenticated) return;
    if (!orgId && !currentUser?.orgId) return;
    if (loadingSucursales) return;
    cargarDatos();
  }, [sucursalSeleccionada?.id, isAuthenticated, orgId, currentUser?.orgId, loadingSucursales]);

  /**
   * Función principal de carga de datos
   */
  const cargarDatos = async () => {
    const loadSeq = ++loadSeqRef.current;
    try {
      setLoading(true);
      setErroresMetricas({});
      
      const sucursalId = sucursalSeleccionada?.id || null;
      
      const [metricasResult] = await Promise.allSettled([
        dashboardService.obtenerMetricasEjecutivas()
      ]);

      if (loadSeq !== loadSeqRef.current) {
        return;
      }

      const errores = {};
      let datosReales = {
        ventasHoy: null,
        cantidadVentasHoy: null,
        gananciasHoy: null,
        productosDestacados: [],
        clientesDestacados: []
      };
      let mesState = {
        cantidadVentas: null,
        facturacion: null,
        ganancias: null,
        gastos: null
      };
      let labelMes = '';

      if (metricasResult.status === 'fulfilled') {
        const m = metricasResult.value;
        labelMes = m.periodoMes
          ? `${m.periodoMes.inicio} → ${m.periodoMes.fin}`
          : '';

        if (m.hoy?.ok) {
          datosReales = m.hoy.data;
        } else {
          errores.hoy = m.hoy?.error || 'No se pudieron cargar las métricas de hoy';
        }

        if (m.ventasMes?.ok) {
          mesState.cantidadVentas = m.ventasMes.cantidadVentas;
          mesState.facturacion = m.ventasMes.facturacion;
        } else {
          errores.ventasMes = m.ventasMes?.error || 'Ventas del mes no disponibles';
        }

        if (m.gananciasMes?.ok) {
          mesState.ganancias = m.gananciasMes.gananciaBruta;
          if (puedeVerGanancias) {
            const consultadoEn = new Date().toISOString();
            setFinanzasMes({
              cobertura: m.gananciasMes.cobertura || null,
              calidadFinanciera: m.gananciasMes.calidadFinanciera || null,
              alertas: m.gananciasMes.alertas || [],
              actualizadoEn: m.gananciasMes.actualizadoEn || consultadoEn
            });
          }
        } else if (puedeVerGanancias) {
          errores.gananciasMes = m.gananciasMes?.error || 'Ganancias del mes no disponibles';
          setFinanzasMes({ cobertura: null, calidadFinanciera: null, alertas: [], actualizadoEn: null });
        }

        if (m.gastosMes?.ok) {
          mesState.gastos = m.gastosMes.total;
        } else {
          errores.gastosMes = m.gastosMes?.error || 'Compras del mes no disponibles';
        }

        if (m.catalogActivation?.total > 0) {
          setCatalogActivation(m.catalogActivation);
        } else {
          setCatalogActivation(null);
        }
      } else {
        errores.general = metricasResult.reason?.message || 'Error al cargar métricas ejecutivas';
      }

      const hoyData = metricasResult.status === 'fulfilled' && metricasResult.value?.hoy?.ok
        ? metricasResult.value.hoy.data
        : null;

      setErroresMetricas(errores);
      setPeriodoMesLabel(labelMes);
      setMetricasMes(mesState);

      const hayErroresCriticos = Boolean(errores.general || errores.hoy);
      if (hayErroresCriticos) {
        if (dashboardRetryCountRef.current < 2) {
          dashboardRetryCountRef.current += 1;
          setTimeout(() => {
            if (loadSeq === loadSeqRef.current) {
              cargarDatos();
            }
          }, dashboardRetryCountRef.current === 1 ? 450 : 900);
        } else if (!dashboardErrorNotifiedRef.current) {
          dashboardErrorNotifiedRef.current = true;
          toast.error('No se pudieron obtener todas las métricas del dashboard');
        }
      } else {
        dashboardRetryCountRef.current = 0;
        dashboardErrorNotifiedRef.current = false;
      }

      const ventasHoyNum = datosReales.ventasHoy ?? 0;
      const cantidadHoyNum = datosReales.cantidadVentasHoy ?? 0;
      const gananciasHoyNum = datosReales.gananciasHoy ?? 0;

      const ticketPromedio = cantidadHoyNum > 0
        ? ventasHoyNum / cantidadHoyNum
        : 0;

      const margenPromedio = puedeVerGanancias && ventasHoyNum > 0
        ? (gananciasHoyNum / ventasHoyNum) * 100
        : 0;

      setStats({
        totalProductos: hoyData?.totalProductos ?? 0,
        ventasHoy: ventasHoyNum,
        cantidadVentasHoy: cantidadHoyNum,
        gananciasHoy: puedeVerGanancias ? gananciasHoyNum : 0,
        stockBajo: hoyData?.stockBajo ?? 0,
        ticketPromedio,
        margenPromedio: puedeVerGanancias ? margenPromedio : 0
      });

      if (puedeVerGanancias && !errores.hoy) {
        await cargarComparativos(sucursalId);
      }

      await cargarDeudaEjecutiva();

    } catch (error) {
      console.error('❌ Error cargando dashboard:', error);
      setErroresMetricas({ general: error.message || 'Error inesperado' });
      if (!dashboardErrorNotifiedRef.current) {
        dashboardErrorNotifiedRef.current = true;
        toast.error('No se pudieron obtener datos del dashboard en este momento');
      }
    } finally {
      if (loadSeq === loadSeqRef.current) {
        setLoading(false);
      }
    }
  };

  // Cargar resumen de pedidos de pre-venta (vista rápida)
  useEffect(() => {
    (async () => {
      try {
        if (!preventaSesion?.id && !preventaSesion?.zona_id) return;
        const { pedidos } = await preventaService.obtenerResumenSesion(preventaSesion.id || preventaSesion.zona_id);
        setResumenPreventa(Array.isArray(pedidos) ? pedidos : []);
      } catch (e) {
        setResumenPreventa([]);
      }
    })();
  }, [preventaSesion, wizardOpen]);

  const confirmarPreventaPedido = async (p) => {
    try {
      if (!sucursalSeleccionada?.id) {
        toast.error('Selecciona una sucursal para confirmar');
        return;
      }
      if (!p?.items || p.items.length === 0) {
        toast.error('El pedido no tiene items');
        return;
      }
      setConfirmandoId(p.id || 'tmp');
      // Validar stock por item
      const insuf = [];
      for (const it of p.items) {
        try {
          const stock = await productosService.consultarStockEnSucursal(it.producto_id, sucursalSeleccionada.id);
          const disponible = parseInt(stock?.cantidad || stock?.stock || stock?.stock_actual || 0);
          if (disponible < (it.cantidad || 0)) {
            insuf.push({ nombre: it.nombre, requerido: it.cantidad, disponible });
          }
        } catch (_) {}
      }
      if (insuf.length > 0) {
        toast.error(`Stock insuficiente en ${insuf.length} producto(s)`);
        return;
      }
      // Construir venta y detalles
      const detalles = p.items.map(it => ({ producto_id: it.producto_id, cantidad: it.cantidad, sucursal_id: sucursalSeleccionada.id }));
      const total = p.items.reduce((acc, it) => acc + (parseFloat(it.precio_venta || it.precio || 0) || 0) * (parseFloat(it.cantidad || 0) || 0), 0);
      const venta = {
        cliente_id: p.cliente_id || p.cliente?.id || null,
        total,
        notas: p.notas || '',
        metodo_pago: 'pendiente',
        estado: 'pendiente'
      };
      await ventasService.crear(venta, detalles, sucursalSeleccionada.id);
      // Marcar en preventa como confirmado (best effort)
      try { if (p.id) await preventaService.confirmarPedido(p.id); } catch (_) {}
      toast.success('Pre-venta confirmada como venta');
      // Quitar de la lista local
      setResumenPreventa(prev => prev.filter(x => (x.id || x) !== (p.id || p)));
    } catch (e) {
      toast.error('Error al confirmar la pre-venta');
      console.error('Confirmar pre-venta error:', e);
    } finally {
      setConfirmandoId(null);
    }
  };

  /**
   * Cargar datos comparativos para mostrar tendencias
   */
  const cargarComparativos = async (sucursalId) => {
    try {
      const hoy = new Date();
      const ayer = new Date(hoy);
      ayer.setDate(ayer.getDate() - 1);

      const semanaAnterior = new Date(hoy);
      semanaAnterior.setDate(semanaAnterior.getDate() - 7);

      // Obtener datos de ayer
      const datosAyer = await reportesService.obtenerReporteVentas({
        fechaInicio: ayer.toISOString().split('T')[0],
        fechaFin: ayer.toISOString().split('T')[0],
        sucursal_id: sucursalId
      });

      // Obtener datos de la semana anterior
      const datosSemanaAnterior = await reportesService.obtenerReporteVentas({
        fechaInicio: semanaAnterior.toISOString().split('T')[0],
        fechaFin: semanaAnterior.toISOString().split('T')[0],
        sucursal_id: sucursalId
      });

      setComparativos({
        ventasAyer: datosAyer?.resumen?.totalVentas || 0,
        gananciasAyer: datosAyer?.resumen?.ganancia || 0,
        ventasSemanaAnterior: datosSemanaAnterior?.resumen?.totalVentas || 0
      });

    } catch (error) {
      console.warn('⚠️ Error cargando comparativos:', error);
      // No mostrar error al usuario, usar valores por defecto
    }
  };

  const cargarDeudaEjecutiva = async () => {
    setLoadingDeudaEjecutiva(true);
    setErrorDeudaEjecutiva(null);
    try {
      const [config, cuentasRaw] = await Promise.all([
        configuracionService.obtener(),
        ventasService.obtenerCuentasPorCobrar()
      ]);
      const diasConfig = normalizarDeudaVencidaDias(config?.pos_politicas?.deuda_vencida_dias);
      const cuentas = parseCuentasPorCobrarResponse(cuentasRaw);
      const built = buildResumenDeudaEjecutivo(cuentas, diasConfig);
      setDeudaEjecutiva({
        resumen: built.resumen,
        clientes: built.clientes,
        diasCriterio: built.diasCriterio
      });
    } catch (error) {
      console.error('[Dashboard] Error cargando deuda ejecutiva:', error);
      setErrorDeudaEjecutiva(error?.message || 'No se pudieron cargar las deudas de clientes');
      setDeudaEjecutiva({
        resumen: { cantidadClientes: 0, totalAdeudado: 0, totalVencido: 0 },
        clientes: [],
        diasCriterio: 30
      });
    } finally {
      setLoadingDeudaEjecutiva(false);
    }
  };

  /**
   * Calcular porcentaje de cambio
   */
  const calcularCambio = (actual, anterior) => {
    if (anterior === 0) return { porcentaje: 0, esPositivo: true };
    
    const cambio = ((actual - anterior) / anterior) * 100;
    return {
      porcentaje: Math.abs(cambio).toFixed(1),
      esPositivo: cambio >= 0
    };
  };

  /**
   * Función para formatear moneda mejorada
   */
  const formatMoney = (amount) => {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '$0.00';
    }
    return '$' + amount.toLocaleString('es-AR', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    });
  };

  const renderMetricValue = (value, { money = false, suffix = '' } = {}) => {
    if (value === null || value === undefined) {
      return <span className="text-sm text-e0e3-muted italic">No disponible</span>;
    }
    const texto = money ? formatMoney(value) : `${value}${suffix}`;
    return <span>{texto}</span>;
  };

  const hayErroresVisibles = Object.keys(erroresMetricas).length > 0;

  /**
   * Componente para mostrar cambios con iconos
   */
  const CambioIndicador = ({ actual, anterior, esMoneda = false }) => {
    const cambio = calcularCambio(actual, anterior);
    
    return (
      <div className={`text-xs flex items-center mt-1 ${
        cambio.esPositivo ? 'text-green-600' : 'text-red-600'
      }`}>
        {cambio.esPositivo ? <FaArrowUp /> : <FaArrowDown />}
        <span className="ml-1">
          {cambio.porcentaje}% vs ayer
        </span>
      </div>
    );
  };

  // Estado de carga
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
        <p className="ml-4 text-e0e3-muted">Cargando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6 max-w-[100vw] overflow-x-hidden">
      {/* Encabezado */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-e0e3-foreground">
            🏪 Dashboard {sucursalSeleccionada?.nombre || '—'}
          </h1>
          <p className="text-e0e3-muted flex items-center">
            <FaCalendarAlt className="mr-2" />
            {new Date().toLocaleDateString('es-AR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
            {sucursalSeleccionada && (
              <span className="ml-4 flex items-center">
                <FaStore className="mr-1" />
                {sucursalSeleccionada.nombre}
              </span>
            )}
          </p>
        </div>
        
        <Link to="/punto-venta">
          <Button
            color="primary"
            size="lg"
            icon={<FaShoppingCart />}
          >
            Punto de Venta
          </Button>
        </Link>
      </div>

      <section
        aria-label="Centro de Control"
        className="rounded-xl border border-amber-500/35 bg-gradient-to-r from-amber-500/10 via-e0e3-surface/60 to-e0e3-surface/40 p-5 lg:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        data-testid="dashboard-centro-control-cta"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h2 className="text-lg lg:text-xl font-semibold text-e0e3-foreground">Centro de Control</h2>
            <span className="inline-flex items-center rounded-full border border-amber-400/50 bg-amber-500/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-100">
              Novedad
            </span>
          </div>
          <p className="text-sm text-e0e3-muted leading-snug max-w-2xl">
            0E3 detecta qué necesita atención y te lleva directamente a resolverlo.
          </p>
        </div>
        <Link to="/centro-operaciones" className="shrink-0">
          <Button color="primary" size="lg" icon={<FaClipboardCheck />}>
            Abrir Centro de Control
          </Button>
        </Link>
      </section>

      <section
        aria-label="Centro Comercial"
        className="rounded-xl border border-e0e3-accent/40 bg-gradient-to-r from-e0e3-accent/12 via-e0e3-surface/60 to-e0e3-surface/40 p-5 lg:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        data-testid="dashboard-centro-comercial-cta"
      >
        <div className="min-w-0">
          <h2 className="text-lg lg:text-xl font-semibold text-e0e3-foreground">Centro Comercial</h2>
          <p className="text-sm text-e0e3-muted leading-snug max-w-2xl mt-1">
            Listas, promociones, combos y material para compartir.
          </p>
        </div>
        <Link to="/centro-comercial" className="shrink-0">
          <Button color="primary" size="lg" icon={<FaCentroComercial />}>
            Abrir Centro Comercial
          </Button>
        </Link>
      </section>

      {hayErroresVisibles && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="font-medium text-amber-200">Algunas métricas no se pudieron cargar</p>
            <ul className="mt-1 text-sm text-amber-100/90 list-disc list-inside">
              {Object.entries(erroresMetricas).map(([key, msg]) => (
                <li key={key}>{msg}</li>
              ))}
            </ul>
          </div>
          <Button color="secondary" size="sm" onClick={() => cargarDatos()}>
            Reintentar
          </Button>
        </div>
      )}

      {/* Métricas operativas del día */}
      <section aria-label="Métricas del día">
        <h2 className="sr-only">Métricas operativas del día</h2>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${puedeVerGanancias ? 'xl:grid-cols-4' : 'xl:grid-cols-3'} gap-5 lg:gap-6`}>
        <Card>
          <div className="flex items-center min-h-[6.5rem] lg:min-h-[7.5rem]">
            <div className="p-4 rounded-2xl bg-blue-500/15 text-blue-300 shrink-0">
              <FaDollarSign size={28} />
            </div>
            <div className="ml-5 flex-1 min-w-0">
              <p className="text-sm font-medium uppercase tracking-wide text-e0e3-muted">Ventas hoy</p>
              <p className="text-3xl font-bold text-e0e3-foreground mt-1 truncate">
                {erroresMetricas.hoy ? renderMetricValue(null) : formatMoney(stats.ventasHoy)}
              </p>
              <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mt-2">
                <p className="text-sm text-blue-300">
                  {erroresMetricas.hoy ? '—' : `${stats.cantidadVentasHoy} ventas`}
                </p>
                {!erroresMetricas.hoy && (
                  <CambioIndicador
                    actual={stats.ventasHoy}
                    anterior={comparativos.ventasAyer}
                    esMoneda={true}
                  />
                )}
              </div>
            </div>
          </div>
        </Card>

        {puedeVerGanancias && (
          <Card>
            <div className="flex items-center min-h-[6.5rem] lg:min-h-[7.5rem]">
              <div className="p-4 rounded-2xl bg-emerald-500/15 text-emerald-300 shrink-0">
                <FaTrendingUp size={28} />
              </div>
              <div className="ml-5 flex-1 min-w-0">
                <p className="text-sm font-medium uppercase tracking-wide text-e0e3-muted">Ganancias hoy</p>
                <p className="text-3xl font-bold text-e0e3-foreground mt-1 truncate">
                  {erroresMetricas.hoy ? renderMetricValue(null) : formatMoney(stats.gananciasHoy)}
                </p>
                <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mt-2">
                  <p className="text-sm text-emerald-300 flex items-center">
                    <FaPercent className="mr-1" size={10} />
                    {erroresMetricas.hoy ? '—' : `${stats.margenPromedio.toFixed(1)}% margen`}
                  </p>
                  {!erroresMetricas.hoy && (
                    <CambioIndicador
                      actual={stats.gananciasHoy}
                      anterior={comparativos.gananciasAyer}
                      esMoneda={true}
                    />
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card>
          <div className="flex items-center min-h-[6.5rem] lg:min-h-[7.5rem]">
            <div className="p-4 rounded-2xl bg-purple-500/15 text-purple-300 shrink-0">
              <FaFileInvoiceDollar size={28} />
            </div>
            <div className="ml-5 flex-1 min-w-0">
              <p className="text-sm font-medium uppercase tracking-wide text-e0e3-muted">Ticket promedio</p>
              <p className="text-3xl font-bold text-e0e3-foreground mt-1 truncate">
                {erroresMetricas.hoy ? renderMetricValue(null) : formatMoney(stats.ticketPromedio)}
              </p>
              <p className="text-sm text-purple-300 mt-2">
                {erroresMetricas.hoy
                  ? '—'
                  : (stats.cantidadVentasHoy > 0
                    ? `${stats.cantidadVentasHoy} ventas hoy`
                    : 'Sin ventas hoy')}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center min-h-[6.5rem] lg:min-h-[7.5rem]">
            <div className={`p-4 rounded-2xl shrink-0 ${
              stats.stockBajo > 0
                ? 'bg-red-500/15 text-red-300'
                : 'bg-blue-500/15 text-blue-300'
            }`}>
              <FaBoxOpen size={28} />
            </div>
            <div className="ml-5 flex-1 min-w-0">
              <p className="text-sm font-medium uppercase tracking-wide text-e0e3-muted">Stock crítico</p>
              <p className="text-3xl font-bold text-e0e3-foreground mt-1">
                {erroresMetricas.hoy ? renderMetricValue(null) : stats.stockBajo}
              </p>
              <p className={`text-sm mt-2 ${
                stats.stockBajo > 0 ? 'text-red-300' : 'text-emerald-300'
              }`}>
                {erroresMetricas.hoy
                  ? 'Stock no disponible'
                  : (stats.stockBajo > 0
                    ? `${stats.stockBajo} producto${stats.stockBajo === 1 ? '' : 's'} bajo mínimo`
                    : 'Sin alertas de stock')}
              </p>
            </div>
          </div>
        </Card>
        </div>
      </section>

      <section aria-label="Cuenta corriente y resumen mensual" className="grid grid-cols-1 xl:grid-cols-2 gap-5 lg:gap-6 items-start min-w-0">
      <div className="min-w-0 overflow-hidden">
      <DashboardDeudaEjecutivo
        loading={loadingDeudaEjecutiva}
        error={errorDeudaEjecutiva}
        resumen={deudaEjecutiva.resumen}
        clientes={deudaEjecutiva.clientes}
        diasCriterio={deudaEjecutiva.diasCriterio}
        onReintentar={cargarDeudaEjecutiva}
      />
      </div>

      <Card title="📊 Resumen del mes" icon={<FaChartLine />}>
        {periodoMesLabel && (
          <p className="text-sm text-e0e3-muted mb-5">Período: {periodoMesLabel}</p>
        )}
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5`}>
          <div className="rounded-xl border border-e0e3-border/40 bg-e0e3-surface/40 p-5 lg:p-6 min-h-[7rem] flex flex-col justify-center">
            <p className="text-sm font-medium text-e0e3-muted">Ventas del mes</p>
            <p className="text-2xl lg:text-3xl font-bold text-e0e3-foreground mt-2">
              {renderMetricValue(metricasMes.cantidadVentas, { suffix: ' operaciones' })}
            </p>
            <p className="text-sm text-e0e3-accent mt-2">
              Facturación: {renderMetricValue(metricasMes.facturacion, { money: true })}
            </p>
          </div>
          {puedeVerGanancias && (
            <GananciasFinancieraCard
              titulo="Ganancias del mes"
              valor={renderMetricValue(metricasMes.ganancias, { money: true })}
              cobertura={finanzasMes.cobertura}
              calidadFinanciera={finanzasMes.calidadFinanciera}
              actualizadoEn={finanzasMes.actualizadoEn}
            />
          )}
          {puedeVerGanancias && finanzasMes.cobertura && (
            <CoberturaFinancieraCard cobertura={finanzasMes.cobertura} />
          )}
          {puedeVerGanancias && finanzasMes.calidadFinanciera && (
            <CalidadFinancieraCard
              calidadFinanciera={finanzasMes.calidadFinanciera}
              cobertura={finanzasMes.cobertura}
            />
          )}
          <div className="rounded-xl border border-e0e3-border/40 bg-e0e3-surface/40 p-5 lg:p-6 min-h-[7rem] flex flex-col justify-center">
            <p className="text-sm font-medium text-e0e3-muted">Compras del mes</p>
            <p className="text-2xl lg:text-3xl font-bold text-e0e3-foreground mt-2">
              {renderMetricValue(metricasMes.gastos, { money: true })}
            </p>
            <p className="text-xs text-e0e3-muted mt-2">Inversión en mercadería</p>
          </div>
          <div className="rounded-xl border border-e0e3-border/40 bg-e0e3-surface/40 p-5 lg:p-6 min-h-[7rem] flex flex-col justify-center">
            <p className="text-sm font-medium text-e0e3-muted">Catálogo activo</p>
            <p className="text-2xl lg:text-3xl font-bold text-e0e3-foreground mt-2">
              {erroresMetricas.hoy ? renderMetricValue(null) : stats.totalProductos}
            </p>
            <p className="text-xs text-e0e3-muted mt-2">Productos en sucursal</p>
          </div>
        </div>
        {puedeVerGanancias && finanzasMes.alertas?.length > 0 && (
          <div className="mt-5 space-y-2">
            {finanzasMes.alertas.map((alerta) => (
              <div
                key={alerta.tipo}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100"
              >
                <FaExclamationTriangle className="shrink-0" />
                <span>{alerta.mensaje}</span>
                {alerta.enlace && (
                  <Link to={alerta.enlace} className="font-semibold underline hover:text-amber-50">
                    {alerta.tipo === 'productos_sin_costo'
                      ? 'Ver productos sin costo registrado'
                      : 'Ver detalle'}
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
      </section>

      {catalogActivation?.total > 0 && (
        <CatalogActivationMetrics metrics={catalogActivation} />
      )}

      {/* Acciones rápidas — operaciones secundarias (PV/Reportes/Stock en header) */}
      <Card title="Operaciones adicionales" icon={<FaPlus />}>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:gap-3 xl:grid-cols-4">
          {canPuntoVenta && (
            <Link to="/punto-venta" className="e0e3-quick-chip e0e3-quick-chip--venta">
              <FaShoppingCart size={16} aria-hidden />
              <span>Nueva Venta</span>
              <span className="e0e3-quick-chip-meta">{stats.cantidadVentasHoy} hoy</span>
            </Link>
          )}

          <Link to="/productos/nuevo" className="e0e3-quick-chip e0e3-quick-chip--producto">
            <FaBoxOpen size={16} aria-hidden />
            <span>Agregar Producto</span>
            <span className="e0e3-quick-chip-meta">{stats.totalProductos} total</span>
          </Link>

          {stats.stockBajo > 0 && (
            <Link to="/productos?filtro=stock_bajo" className="e0e3-quick-chip e0e3-quick-chip--alerta">
              <FaExclamationTriangle size={16} aria-hidden />
              <span>Stock Bajo</span>
              <span className="e0e3-quick-chip-meta">{stats.stockBajo} alertas</span>
            </Link>
          )}

          {isDemoUser && (
            <Link to="/cajero" className="e0e3-quick-chip e0e3-quick-chip--demo">
              <FaShoppingCart size={16} aria-hidden />
              <span>Probar Caja</span>
              <span className="e0e3-quick-chip-meta">Demo</span>
            </Link>
          )}

          <button type="button" onClick={() => setModalPreventaOpen(true)} className="e0e3-quick-chip e0e3-quick-chip--preventa">
            <FaFileInvoiceDollar size={16} aria-hidden />
            <span>Pre-venta</span>
            <span className="e0e3-quick-chip-meta">Por zona</span>
          </button>

          <button type="button" onClick={() => setZonasModalOpen(true)} className="e0e3-quick-chip e0e3-quick-chip--zonas">
            <FaUser size={16} aria-hidden />
            <span>Zonas</span>
            <span className="e0e3-quick-chip-meta">Administrar</span>
          </button>

          <button type="button" onClick={() => setModalRepartoOpen(true)} className="e0e3-quick-chip e0e3-quick-chip--reparto">
            <FaTruck size={16} aria-hidden />
            <span>Reparto</span>
            <span className="e0e3-quick-chip-meta">Entregas</span>
          </button>
        </div>
      </Card>

      {/* Vista rápida: Pedidos de Pre-venta (borradores) */}
      {resumenPreventa.length > 0 && (
        <Card title="📝 Pedidos de Pre-venta (borradores)" icon={<FaFileInvoiceDollar />}>
          <div className="max-h-64 overflow-auto text-sm">
            {resumenPreventa.slice(0, 10).map((p, idx) => (
              <div key={p.id || idx} className="flex items-center justify-between border-b py-2">
                <div>
                  <div className="font-medium text-gray-800">{p.cliente?.nombre || p.cliente_nombre || 'Cliente'}</div>
                  <div className="text-gray-500">Items: {(p.items?.length) || (Array.isArray(p.items) ? p.items.length : 0)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" color="primary" onClick={() => confirmarPreventaPedido(p)} loading={confirmandoId === (p.id || 'tmp')}>
                    Confirmar
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {resumenPreventa.length > 10 && (
            <div className="text-xs text-gray-500 mt-2">Mostrando 10 de {resumenPreventa.length}</div>
          )}
        </Card>
      )}

      {/* Iniciar Pre-venta */}
      <IniciarPreVentaModal
        isOpen={modalPreventaOpen}
        onClose={() => setModalPreventaOpen(false)}
        onStart={(sesion) => { setPreventaSesion(sesion); setWizardOpen(true); }}
      />
      <ZonasABMModal isOpen={zonasModalOpen} onClose={() => setZonasModalOpen(false)} />
      {/* Wizard Pre-venta */}
      <PreVentaWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        sesion={preventaSesion}
        sucursalId={sucursalSeleccionada?.id || null}
      />
      {/* Iniciar Reparto */}
      <IniciarRepartoModal
        isOpen={modalRepartoOpen}
        onClose={() => setModalRepartoOpen(false)}
        sucursalId={sucursalSeleccionada?.id || null}
        sucursalesDisponibles={[]}
        onStart={(sesion) => { setRepartoSesion(sesion); setRepartoWizardOpen(true); }}
      />
      <RepartoWizardMovil
        isOpen={repartoWizardOpen}
        onClose={() => setRepartoWizardOpen(false)}
        sesion={repartoSesion}
        sucursalId={sucursalSeleccionada?.id || null}
      />
     </div>
  );
};

export default Dashboard;